import React, { useContext, useMemo, useState } from 'react';
import { BillingProductCard } from '../Summary/BillingProductCard';
import { useHistory } from 'react-router';
import { useTrialProgress } from '../../Hooks/useTrialProgress';
import {
  TrialPeriodProgress,
  TrialPeriodProgressTheme,
} from '../Common/TrialPeriodProgress';
import { Service, Subscription } from '~/types/models';
import { getMonthlyPrice, mapAddOnToAddOnItem } from '../../utils';
import {
  queryKeys as invoiceQueryKeys,
  useInvoiceInfo,
} from '../../Hooks/useInvoiceInfo';
import usePlans from '~/react-hooks/use-plans';
import { Button } from '@vimily/bonjoro-ui-components-v2/Button';
import { ButtonVariant } from '@vimily/bonjoro-ui-components-v2';
import AddAppSumoCodesModal from '~/App/Modules/Billing/Components/AddAppSumoCodesModal';
import { useQueryClient } from 'react-query';
import { queryKeys as subscriptionQueryKeys } from '../../Hooks/useSubscription';
import FlashContext from '~/App/Components/Flash/Context';
import { ServiceIconImage } from '~/App/Modules/Settings/NewTeam/Components/ManageTeams/ServiceIconImage';
import { getAddOnMonthlyPrice } from '../../Hooks/useAddOns';

export type ProductSummaryProps = {
  subscription: Subscription;
};

export function ProductSummary({ subscription }: ProductSummaryProps) {
  const history = useHistory();
  const { data: invoiceInfo } = useInvoiceInfo({});
  const plans = usePlans();
  const queryClient = useQueryClient();
  const hasMultiplePlans = plans.length > 1;
  const { createFlash } = useContext(FlashContext);

  // Manually show the monthly price of Add-ons for Product summary
  const addons = useMemo(() => {
    if (!subscription.addons) return [];

    return mapAddOnToAddOnItem(subscription.addons, subscription.service).map(
      (addOnItem) => {
        return {
          ...addOnItem,
          price: getAddOnMonthlyPrice({
            price: addOnItem?.price,
            billingCycle: subscription?.billing_cycle,
          }),
        };
      }
    );
  }, [subscription]);

  //trial periods of plans are just the same.
  const { progress, maxProgress } = useTrialProgress(subscription);

  const hidePricingDetails = subscription?.is_free || subscription?.cancelled;

  const basicPrice = getMonthlyPrice({
    price: Number(subscription?.original_price),
    billingCycle: subscription?.billing_cycle,
  });

  const [showAddAppSumoCodesModal, setShowAddAppSumoCodesModal] =
    useState(false);

  const onAddAppSumoCodeModalClose = () => setShowAddAppSumoCodesModal(false);

  const onAddAppSumoCodesSuccess = () => {
    queryClient.invalidateQueries(subscriptionQueryKeys.lists());
    queryClient.invalidateQueries(invoiceQueryKeys.detail);

    onAddAppSumoCodeModalClose();

    createFlash({
      id: 'add-app-sumo-codes-success',
      title: 'Success',
      message: 'AppSumo codes added successfully.',
    });
  };

  const labelComponent = () => {
    if (!subscription?.service) return <>{subscription.name}</>;

    return (
      <div className='flex flex-wrap flex-row gap-x-3 gap-y-3 items-center'>
        <ServiceIconImage
          className='!w-[30px] !h-[30px] !p-1 !v2-border-neutral-300 !cursor-default'
          service={subscription?.service}
          active
        />
        {subscription?.name}
      </div>
    );
  };

  const maxCodes = subscription?.metadata?.app_sumo?.max_codes;
  const canAddAppSumoCodes =
    subscription?.features?.canAddAppSumoCode && maxCodes && maxCodes >= 1;

  return (
    <>
      <BillingProductCard
        label={labelComponent()}
        basicPrice={`$${basicPrice}`}
        usages={subscription.usages}
        addOns={addons}
        billingDate={
          hasMultiplePlans ? undefined : invoiceInfo?.upcoming_invoice?.date
        }
        hideTotalPrice={subscription?.cancelled}
        hidePrice={subscription?.cancelled}
        totalPrice={`$${subscription.total}`}
        progressComponent={
          <>
            {subscription?.cancelled && (
              <span>{subscription?.cancelled_days_left_text}</span>
            )}
            {progress != undefined && (
              <TrialPeriodProgress
                progress={progress}
                maxProgress={maxProgress}
                className='xmd:mr-4'
                theme={
                  subscription?.service === Service.VideoMessaging
                    ? TrialPeriodProgressTheme.Blue
                    : subscription?.service === Service.Testimonials
                    ? TrialPeriodProgressTheme.Magenta
                    : undefined
                }
              />
            )}
          </>
        }
        discountFormat={
          hidePricingDetails ? undefined : subscription.discount_formatted
        }
        onManageClick={() =>
          history.push(`/billing/subscription/${subscription.slug}`)
        }
        secondaryCta={
          canAddAppSumoCodes && (
            <Button
              className='py-2'
              variant={ButtonVariant.Secondary}
              onClick={() => setShowAddAppSumoCodesModal(true)}
            >
              Add AppSumo codes
            </Button>
          )
        }
      />
      {canAddAppSumoCodes ? (
        <AddAppSumoCodesModal
          title='Add AppSumo codes'
          open={showAddAppSumoCodesModal}
          maxCodes={subscription?.metadata?.app_sumo?.max_codes}
          onModalClose={onAddAppSumoCodeModalClose}
          onAddSuccess={onAddAppSumoCodesSuccess}
        />
      ) : undefined}
    </>
  );
}
